import { CaseData } from '../types/case';
import { debugError } from './calcDebug';
import { runWithCalcRuntimeContext, type RecalculateOptions } from './calcRuntimeContext';
import { validateInputVersioned, calcDocumentsVersioned } from './versions/versionRouter';

/**
 * Initializes the calculated values for a case by clearing computed values and errors.
 * 
 * @param caseData - The case data to initialize
 * @returns A new CaseData object with initialized values
 */
function init(caseData: CaseData): CaseData {
    return {
        ...caseData,
        metadata: {
            ...caseData.metadata,
            errorLevel: undefined,
            errors: [],
            errorLocation: undefined
        },
        validation: {
            ...caseData.validation,
            // Preserve visited flags and other validation properties
        },
        documents: {
            summary: undefined,
            worksheet1: undefined,
            highIncomeEstimator: caseData.documents?.highIncomeEstimator?.extrapolationMethod != null
                ? { extrapolationMethod: caseData.documents.highIncomeEstimator.extrapolationMethod }
                : undefined,
            stepDownCases: undefined
        }
    };
}




/**
 * The calculation itself, with nothing alimony-specific in it. Alimony scenario work calls this
 * directly (a scenario is a case with its own spousal support, and must never trigger another
 * alimony refresh); `recalculate()` in `./index` adds the refresh on top.
 */
export function recalculateEngine(caseData: CaseData, options?: RecalculateOptions): CaseData {
    const runtime: RecalculateOptions = {
        environment: options?.environment ?? 'client',
        consolidatedLookups: options?.consolidatedLookups,
    };
    return runWithCalcRuntimeContext(runtime, () => recalculateCore(caseData));
}

export function recalculateCore(caseData: CaseData): CaseData {
    // debugLog('Validating case in recalculate() function...', caseData);
    var rv = init(caseData);
    rv = validateInputVersioned(rv);
    // if (!!rv.metadata?.errorLevel) {
    //     // prevent any calculations if there are errors
    //     debugLog('Validation errors, returning early', rv.metadata?.errorLevel);
    //     debugLog('Validation errors', rv.metadata?.errors);
    //     return rv;
    // }

    // debugLog('Validation passed, calculating values...');

    try {
        calcDocumentsVersioned(rv);
    } catch (error) {
        // Log the error
        debugError('Error during calculations:', error);

        // Set error metadata
        rv.metadata = {
            ...rv.metadata,
            errorLevel: 'calculationError',
            errors: [...(rv.metadata?.errors ?? []), `Calculation error: ${error instanceof Error ? error.message : String(error)}`],
            errorLocation: null
        };

        // Re-throw the error so ErrorBoundary can catch it
        // Note: ErrorBoundary will only catch if error occurs during render, not in event handlers
        // But we re-throw anyway so it can be caught at a higher level if needed
        throw error;
    }

    return rv;
}
